/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { subDays } from "date-fns";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import useAccountPageParams from "../../../../hooks/useAccountPageParams";
import { useWorkspaceObjectSchema } from "../../../../hooks/QueryHooks";
import { isMetricLikeField } from "../../../../shared/table/objectSearch";
import EmptyTabState from "../../../../shared/ui/EmptyTabState";
import { Users } from "lucide-react";
import { LegacyColumn } from "../../../../shared/table/legacyTanStackAdapter";
import TanStackTable from "../../../../shared/table/TanStackTable";
import useObjectSearchTable from "../../../../shared/table/useObjectSearchTable";
import useUsersTableStore from "../../../../store/usersTable/usersTableStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import dateConverter from "../../../../utils/dateConverter";
import parseTableData from "../../../../utils/parseTable";
import UsersToolbar from "./components/UsersToolbar";

const AccountUsers = () => {
  const { usersTable, updateUsersTable } = useUsersTableStore();
  const params: any = useAccountPageParams();
  const workspace = useWorkspaceStore((state) => state.workspace);

  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const traits = useMemo(
    () => workspaceSchema?.fieldsByObjectSlug?.users || { list: [], obj: {} },
    [workspaceSchema?.fieldsByObjectSlug],
  );
  const allColumns = useMemo(() => ({ ...traits?.obj }), [traits?.obj]);

  const [selectedCols, setSelectedCols] = useState<any[]>(
    (usersTable.selected_fields || []).filter((field: string) => !isMetricLikeField(field)),
  );
  const [showHidden, setShowHidden] = useState(false);
  const [metric, setMetric] = useState<any>({ id: -1, name: "" });

  const filteredTraits = useMemo(
    () => traits?.list?.filter((item: any) => !item.exclude_display),
    [traits?.list],
  );
  const filteredMetrics = useMemo(() => [], []);

  const tableHeight = useMemo(() => (window.innerHeight - 230), []);

  const legacyColumns = useMemo<LegacyColumn[]>(() => (
    [...((usersTable?.selected_fields || []).filter((field: string) => !isMetricLikeField(field)))]
      .reduce((columns: LegacyColumn[], item: string) => {
        if (!allColumns[item] || allColumns[item]?.exclude_display) return columns;

        const column: LegacyColumn = {
          key: allColumns[item].name,
          dataType: allColumns[item].data_type,
          title: allColumns[item].display_name,
          isSortable: true,
          attributeType: allColumns[item].attribute_type,
          sortType: usersTable?.sort?.field === item ? "property" : undefined,
        };

        if (item.includes("name")) {
          columns.unshift(column);
        } else {
          columns.push(column);
        }
        return columns;
      }, [])
  ), [usersTable?.selected_fields, usersTable?.sort?.field, allColumns]);

  const scopedFilter = useMemo(() => {
    const predicates: any[] = [{
      field: "accounts.external_id",
      operator: "eq",
      type: "string",
      value: params.accountId,
      entity_type: "column",
    }];

    if (usersTable?.filter?.predicates?.length) {
      predicates.unshift(usersTable.filter);
    }

    return {
      query_type: "AND",
      predicates,
    };
  }, [params.accountId, usersTable?.filter]);

  const {
    rows,
    total,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setSort: setTableSort,
    setPageIndex,
    isLoading,
    isFetching,
  } = useObjectSearchTable({
    workspaceId: workspace?.id,
    entityType: "users",
    enabled: !!workspace?.id && !!traits?.obj,
    initialPageSize: usersTable.per_page,
    initialSort: {
      field: !isMetricLikeField(usersTable?.sort?.field) ? usersTable?.sort?.field : "users.last_seen",
      order: usersTable?.sort?.order,
    },
    queryOptions: {
      filter: scopedFilter,
      selectedFields: selectedCols,
      fallbackSortField: !isMetricLikeField(usersTable?.sort?.field) ? usersTable?.sort?.field : "users.last_seen",
    },
    parseRows: (rawRows) => parseTableData({
      data: (rawRows || []).map((record: any) => {
        const row: any = {};
        const fields = record?.fields || {};
        Object.entries(fields).forEach(([key, value]) => {
          row[`users.${key}`] = value;
        });
        if (!row["users.external_id"]) {
          row["users.external_id"] = record?.id || fields?.external_id;
        }
        return row;
      }),
    }),
    resetPageDeps: [
      params.accountId,
      JSON.stringify(usersTable?.filter || {}),
      JSON.stringify(selectedCols || []),
    ],
  });

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const nextSort = (nextSorting || []).slice(0, 1)?.[0];
    if (!nextSort?.id) return;

    const nextField = nextSort.id;
    const nextOrder = nextSort.desc ? "desc" : "asc";

    updateUsersTable({
      sort: {
        field: nextField,
        order: nextOrder,
        type: "property",
      },
    });
    setTableSort({
      field: nextField,
      order: nextOrder,
    });
  }, [sorting, updateUsersTable, setTableSort]);

  const onSelectColumn = (item: any) => {
    if (item.attribute_type === "metric_traits") return;

    if (selectedCols.includes(item.name)) {
      setSelectedCols((state: any) => state.filter((name: any) => name !== item.name));
    } else {
      setSelectedCols((state: any) => [...state, item.name]);
    }
  };

  useEffect(() => {
    updateUsersTable({ selected_fields: selectedCols, metrics: [] });
  }, [selectedCols, updateUsersTable]);

  useEffect(() => {
    if (usersTable?.sort?.field && !isMetricLikeField(usersTable.sort.field)) {
      setTableSort({
        field: usersTable.sort.field,
        order: usersTable.sort.order,
      });
    }
  }, [usersTable?.sort?.field, usersTable?.sort?.order, setTableSort]);

  useEffect(() => {
    if (metric?.name) {
      const currentDateTime = new Date();
      const val = parseInt(
        metric.name.match(/[0-9]+/) ? metric.name.match(/[0-9]+/)[0] : "1",
        10,
      );
      const calcDate = subDays(currentDateTime, val);
      updateUsersTable({
        filter: {
          predicates: [
            {
              field: "last_seen",
              operator: "range",
              type: "timestamp",
              value: {
                start: dateConverter(calcDate),
                end: dateConverter(currentDateTime),
              },
              entity_type: "column",
            },
          ],
        },
      });
    }
  }, [metric.name]);

  const noResults = !isLoading && !isFetching && rows.length === 0;

  return (
    <div className="relative flex flex-col border rounded-lg overflow-hidden gap-2 bg-white overflow-auto">
      <UsersToolbar
        selectedColumns={selectedCols}
        setSelectedColumns={onSelectColumn}
        showHidden={showHidden}
        setShowHidden={setShowHidden}
        filteredTraits={filteredTraits}
        filteredMetrics={filteredMetrics}
        users={total}
        metric={metric}
        setMetric={setMetric}
      />
      {noResults ? (
        <EmptyTabState
          title="No users yet"
          description="Users associated with this account will appear here."
          icon={<Users size={24} />}
        />
      ) : (
        <TanStackTable
          legacyColumns={legacyColumns}
          data={rows}
          totalData={total}
          itemType="Users"
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={pageCount}
          onPageIndexChange={setPageIndex}
          sorting={sorting}
          onSortingChange={onTableSortingChange}
          loading={isLoading || isFetching}
          height={tableHeight}
        />
      )}
    </div>
  );
};

export default AccountUsers;
